import React, { useContext, useEffect, useState } from 'react';
import { useBackendApiFetch } from 'src/lib/BackendFetchContext';
import { LoginContext } from 'src/lib/LoginContext';

interface IAccountHeaderContext {
  bidCart: number;
  bidsSubmitted: number;
  messages: number;
  notifications: number;
  updateHeaderCounts: () => Promise<void>;
  bidUpdates: number;
  firstName?: string;
  lastName?: string;
  email?: string;
}

export const AccountHeaderContext = React.createContext<Partial<IAccountHeaderContext>>({});
export const AccountHeaderContextProvider = ({ children }) => {
  const [bidCart, setBidCart] = useState(0);
  const [bidsSubmitted, setBidsSubmitted] = useState(0);
  const [messages, setMessages] = useState(0);
  const [notifications, setNotifications] = useState(0);
  const [bidUpdates, setBidUpdates] = useState(0);
  const backendApiFetch = useBackendApiFetch();
  const [{ firstName, lastName }, setName] = useState({ firstName: '', lastName: '' });
  const [email, setEmail] = useState('');

  const { isLoggedIn } = useContext(LoginContext);

  const updateHeaderCounts = async (): Promise<void> => {
    const headerCountResponse = await backendApiFetch('/api/account/headercounts', { credentials: 'include' });
    const headerCountResult = await headerCountResponse.json();
    const activeAuctions = await (await backendApiFetch('/api/auction/activeAuctions', {
      credentials: 'include',
    })).json();

    const allBidCounts: Record<string, number> = await (await backendApiFetch('/api/account/bids/getAllCurrentBidsCount', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        auctionIds: activeAuctions.map((x) => x.AuctionID).join(','),
      }),
    })).json();

    const bidCount = Object.values(allBidCounts).reduce((pv, cv) => pv + cv, 0);

    setMessages(headerCountResult.messages);
    setNotifications(headerCountResult.notifications);
    setBidCart(headerCountResult.cart);
    setBidUpdates(bidUpdates + 1);
    setBidsSubmitted(bidCount);
  };

  const updateAccountInfo = async (): Promise<void> => {
    const accountResp = await backendApiFetch('/api/account/info/accountInfo');
    const emailResp = await backendApiFetch('/api/account/email/getAll');
    const emails = await emailResp.json();
    const primaryEmail = emails.find((e) => e.EmailType === 'Primary');
    const account = await accountResp.json();
    if (account && account.length === 1) {
      setName({ firstName: account[0].FirstName, lastName: account[0].LastName });
    }
    setEmail(primaryEmail && primaryEmail.Email || '');
  }

  useEffect(() => {
    if(!isLoggedIn) {
      setName(() => ({firstName:'', lastName: ''}));
      setEmail(() => '');
      return;
    }
    updateHeaderCounts();
    updateAccountInfo();
  }, [isLoggedIn]);

  return (
    <AccountHeaderContext.Provider value={{
      bidCart,
      bidsSubmitted,
      messages,
      notifications,
      updateHeaderCounts,
      bidUpdates,
      firstName,
      lastName,
      email,
    }}>
      {children}
    </AccountHeaderContext.Provider>
  );
};
